/**@namespace pdfGenerator */

import jsPDF from "jspdf";
import { ShadingPattern } from "jspdf";
import "@/modules/global/utils/fonts/Quicksand-Regular-normal.js";
import "@/modules/global/utils/fonts/Quicksand-Medium-normal.js";
import "@/modules/global/utils/fonts/Quicksand-Light-normal.js";
import "@/modules/global/utils/fonts/Quicksand-Bold-normal.js";
import matric from "@/modules/global/utils/thread-layouts/matric-foundation.json";
import juniorHigh from "@/modules/global/utils/thread-layouts/junior-high.json";
import seniorPrimary from "@/modules/global/utils/thread-layouts/senior-primary.json";
import juniorPrimary from "@/modules/global/utils/thread-layouts/junior-primary.json";
import adultNumeracy from "@/modules/global/utils/thread-layouts/adult-numeracy.json";
import englishJuniorHigh from "@/modules/global/utils/thread-layouts/english-junior-high.json";
import englishSeniorPrimary from "@/modules/global/utils/thread-layouts/english-senior-primary.json";
import { threads, assessmentLevelsMap } from "../utils/threads-by-assessment";
import Chart from "chart.js";

/**
 * A modified user object
 * @class User
 * @param {object} learnerObject
 */
class User {
  constructor(learnerObject) {
    this.firstName = learnerObject.firstName;
    this.lastName = learnerObject.lastName;
    this.email = learnerObject.email;
    this.grade = learnerObject.grade;
    this.project = learnerObject.project;
    this.school = learnerObject.school;
    this.currentAssessment = learnerObject.assessmentStates[0].type;
    this.threads = threads(this.grade, this.currentAssessment);
    this.assessmentsNotCompleted = [];
    this.assessmentTypeLevel = this.getLevel(
      learnerObject.assessmentStates[0].type
    );
    this.assessmentType = learnerObject.assessmentStates[0].type;
    this.assessmentStates = learnerObject.assessmentStates;
    this.subscriptionDate = this.createDateString(learnerObject.createdAt);
    this.reportIssueDate = this.createDateString(new Date());
    this.assessmentResults = this.calcualateThreadAssessmentStatus(
      learnerObject.improvement
        ? learnerObject.improvement[learnerObject.currentSubject]
        : null
    );
    this.assessmentStatus = this.getAssessmentStatus(learnerObject);
    this.greenConcepts = 0;
    this.yellowConcepts = 0;
    this.redConcepts = 0;
    this.darkRedConcepts = 0;
    this.greyConcepts = 0;
    this.currentSubject = learnerObject.currentSubject;
    if (learnerObject.currentSubject === "MATHS") {
      this.subject = "Mathematics";
    } else {
      this.subject = "English";
    }
  }
  getLevel(assessmentType) {
    return assessmentLevelsMap(assessmentType);
  }
  concepts() {
    if (this.assessmentStates[0].type === "MATHS_SENIOR_PRIMARY") {
      return seniorPrimary;
    } else if (this.assessmentStates[0].type === "MATHS_JUNIOR_PRIMARY") {
      return juniorPrimary;
    } else if (this.assessmentStates[0].type === "MATHS_MATRIC_FOUNDATIONS") {
      return matric;
    } else if (this.assessmentStates[0].type === "MATHS_ADULT_NUMERACY") {
      return adultNumeracy;
    } else if (this.assessmentStates[0].type === "ENGLISH_JUNIOR_HIGH") {
      return englishJuniorHigh;
    } else if (this.assessmentStates[0].type === "ENGLISH_SENIOR_PRIMARY") {
      return englishSeniorPrimary;
    } else {
      return juniorHigh;
    }
  }
  conceptMasteryToGrade(mastery) {
    switch (this.assessmentType) {
      case "MATHS_JUNIOR_PRIMARY":
        if (mastery == 0) {
          return 0;
        } else if (mastery == 1) {
          return 1;
        } else if (mastery == 2) {
          return 2;
        } else {
          return 3;
        }
      case "MATHS_SENIOR_PRIMARY":
      case "ENGLISH_SENIOR_PRIMARY":
        if (mastery == 0) {
          return 0;
        } else if (mastery == 1) {
          return 1;
        } else if (mastery == 2) {
          return 3;
        } else {
          return 6;
        }
      case "MATHS_JUNIOR_HIGH":
      case "MATHS_MATRIC_FOUNDATIONS":
      case "ENGLISH_JUNIOR_HIGH":
      default:
        if (mastery == 0) {
          return 0;
        } else if (mastery == 1) {
          return 3;
        } else if (mastery == 2) {
          return 6;
        } else {
          return 9;
        }
    }
  }
  conceptResults(currentThread) {
    let results = [];
    let activeThreadFeedback = this.assessmentStates[0].feedback.find(
      thread => thread.threadId == currentThread
    );
    let activeThreadConcepts = this.concepts()[currentThread];
    if (activeThreadFeedback) {
      activeThreadFeedback.conceptMastery.forEach((concept, index) => {
        let correctConcept = activeThreadConcepts.find(
          concepts => concepts.concept == index + 1
        );
        if (concept === 3) {
          this.greenConcepts += 1;
          results.push({
            name: correctConcept.name,
            mastery: this.conceptMasteryToGrade(concept),
            color: "green"
          });
        } else if (concept === 2) {
          this.yellowConcepts += 1;
          results.push({
            name: correctConcept.name,
            mastery: this.conceptMasteryToGrade(concept),
            color: "yellow"
          });
        } else if (concept === 1) {
          this.redConcepts += 1;
          results.push({
            name: correctConcept.name,
            mastery: this.conceptMasteryToGrade(concept),
            color: "red"
          });
        } else {
          this.darkRedConcepts += 1;
          results.push({
            name: correctConcept.name,
            mastery: this.conceptMasteryToGrade(concept),
            color: "dark-red"
          });
        }
      });
    } else {
      activeThreadConcepts.forEach(element => {
        this.greyConcepts += 1;
        results.push({
          name: element.name,
          color: "grey"
        });
      });
    }

    return results;
  }
  calcualateThreadAssessmentStatus(userAssessmentFeedback) {
    if (!userAssessmentFeedback) return [];
    let initialResults = [];
    let latestResultsDifference = [];
    for (
      let threadIndex = 0;
      threadIndex < this.threads.length;
      threadIndex++
    ) {
      let currentThreadResults = userAssessmentFeedback[threadIndex + 1];
      if (currentThreadResults) {
        const currentThreadResultsLength = currentThreadResults.length;
        initialResults.push(currentThreadResults[0].gradeMastered);
        currentThreadResultsLength > 1
          ? latestResultsDifference.push(
            currentThreadResults[currentThreadResultsLength - 1]
              .gradeMastered - currentThreadResults[0].gradeMastered
          )
          : latestResultsDifference.push(null);
      } else {
        initialResults.push(null);
        latestResultsDifference.push(null);
      }
    }
    return [initialResults, latestResultsDifference];
  }
  createDateString(dateString) {
    let date = new Date(dateString);
    const day = date.getDate();
    const month = this.getMonthStringFromNumber(date.getMonth());
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }
  getMonthStringFromNumber(monthNumber) {
    const Months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ];
    return Months[monthNumber];
  }
  percentageMastered(learnerObject, threadId) {
    let totalGradeLevel = 0;

    if (threadId) {
      let thread = learnerObject.assessmentStates[0].feedback.find(
        thread => thread.threadId == threadId
      );
      if (thread && thread.conceptMastery) {
        thread.conceptMastery.forEach((element, key) => {
          totalGradeLevel += element;
        });
        let maxGradeLevel = 3 * thread.conceptMastery.length;
        let percentage = (totalGradeLevel / maxGradeLevel) * 100;
        return Math.round(percentage);
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }
  trophyColour(learnerObject, threadId) {
    let colour = "";
    let percentage = this.percentageMastered(learnerObject, threadId);

    if (percentage < 75) {
      colour = "None";
    } else if (percentage < 90) {
      colour = "Bronze";
    } else if (percentage < 100) {
      colour = "Silver";
    } else {
      colour = "Gold";
    }

    return `${colour}`;
  }
  getAssessmentStatus(learnerObject) {
    let threadStats = [];
    let assessmentTopLevel = this.topGradeByAssessment(
      learnerObject.assessmentStates[0].type
    );
    for (let i = 0; i < this.threads.length; i++) {
      let threadId = i + 1;
      let assessmentThread = learnerObject.assessmentStates[0].threads.find(
        thread => thread.threadId == threadId
      );
      let assessmentFeedback = learnerObject.assessmentStates[0].feedback.find(
        thread => thread.threadId == threadId
      );
      if (typeof assessmentFeedback === "undefined") {
        assessmentFeedback = {};
      }

      let reassessmentComplete = false;
      let reassessmentCompleteCount = 0;
      let hasImproved = false;
      let yearsImproved = 0;
      if (
        learnerObject.improvement &&
        learnerObject.improvement[learnerObject.currentSubject] &&
        typeof learnerObject.improvement[learnerObject.currentSubject][
        threadId
        ] != "undefined"
      ) {
        let reassessmentCompletedItems = learnerObject.improvement[
          learnerObject.currentSubject
        ][threadId].filter(thread => thread.reassessment === true);
        reassessmentCompleteCount = reassessmentCompletedItems.length;

        learnerObject.improvement[learnerObject.currentSubject][
          threadId
        ].forEach(reassessment => {
          if (reassessment.reassessment === true) {
            reassessmentComplete = true;
            if (reassessment.relGradeMastered > 0) {
              hasImproved = true;
              yearsImproved = Math.abs(reassessment.relGradeMastered);
            }
          }
        });
      }

      let stats = {};
      stats.threadId = threadId;
      stats.threadName = assessmentThread.threadName;
      stats.assessmentComplete =
        assessmentThread && assessmentThread.progress == 100 ? true : false;
      stats.feedback = assessmentFeedback;
      stats.threadLevel = assessmentFeedback.gradeMastered
        ? assessmentFeedback.gradeMastered
        : 0;
      stats.maxThreadLevel = assessmentTopLevel;
      stats.reassessmentComplete = reassessmentComplete;
      stats.reassessmentCompleteCount = reassessmentCompleteCount;
      stats.hasImproved = hasImproved;
      stats.yearsImproved = yearsImproved;
      if (stats.threadLevel >= assessmentTopLevel) {
        stats.trophyAchieved = true;
      } else {
        stats.trophyAchieved = false;
      }

      stats.trophyColour = this.trophyColour(learnerObject, threadId);
      stats.percentageMastered = this.percentageMastered(
        learnerObject,
        threadId
      );

      threadStats.push(stats);
    }

    return threadStats;
  }
  topGradeByAssessment(assessmentName) {
    let topGrade;
    switch (assessmentName) {
      case "MATHS_JUNIOR_PRIMARY":
        topGrade = 3;
        break;
      case "MATHS_SENIOR_PRIMARY":
      case "ENGLISH_SENIOR_PRIMARY":
        topGrade = 6;
        break;
      case "MATHS_JUNIOR_HIGH":
      case "MATHS_ADULT_NUMERACY":
      case "MATHS_MATRIC_FOUNDATIONS":
      case "ENGLISH_JUNIOR_HIGH":
        topGrade = 9;
        break;
      default:
        topGrade = 0;
        break;
    }
    return topGrade;
  }
}
/**
 * Report layout class that initiates a jspdf instance
 * Extends user class
 * @class Report
 * @extends {User}
 * @param {object} learnerObject
 */
class Report extends User {
  constructor(learnerObject, currentSubject) {
    learnerObject.currentSubject = currentSubject;
    super(learnerObject);
    this.pdf = new jsPDF({
      compressPdf: true
    });
    this.verticalHeight = 50;
    this.lineHeight = 1.5;
    this.leftHeight = 30;
    this.rightHeight = 15;
    this.rightMargin = 80;
    this.rightWidth = 125;
    this.leftWidth = 65;
    this.leftMargin = 8;
    this.lastColHeight = 30;
    this.lastColWidth = 60;
    this.lastColMargin = 150;
    this.headingLineHeight = 15;
    this.paragraphLineHeight = 10;
    this.activeColumn = "Left";
    this.goldCrownUrl = null;
    this.greyCrownUrl = null;
    this.projectImageUrl = null;
    this.signitureUrl = null;
    this.chart = this.drawChart();
  }
  setCrownUrl(colour, url) {
    this[`${colour}CrownUrl`] = url;
  }
  incrementLeftHeight(height) {
    this.leftHeight += height;
  }
  incrementRightHeight(height) {
    this.rightHeight += height;
  }
  incrementLastColHeight(height) {
    this.lastColHeight += height;
  }
  boldFont() {
    this.setFontStyle("Bold");
  }
  lightFont() {
    this.setFontStyle("Light");
  }
  mediumFont() {
    this.setFontStyle("Medium");
  }
  regularFont() {
    this.setFontStyle();
  }
  setFontStyle(style = "Regular") {
    this.pdf.setFont(`Quicksand-${style}`, "normal");
  }
  setFontTo32() {
    this.setFontSize(32);
  }
  setFontTo24() {
    this.setFontSize(24);
  }
  setFontTo16() {
    this.setFontSize(16);
  }
  setFontTo14() {
    this.setFontSize(14);
  }
  setFontTo12() {
    this.setFontSize(12);
  }
  setFontTo11() {
    this.setFontSize(11);
  }
  setFontTo10() {
    this.setFontSize(10);
  }
  setFontTo8() {
    this.setFontSize(8);
  }
  setFontSize(size) {
    this.pdf.setFontSize(size);
  }
  shiftLeftMargin() {
    this.leftMargin += 12;
  }
  resetLeftMargin() {
    this.leftMargin = 8;
  }
  resetLeftWidth() {
    this.leftWidth = 65;
  }
  reduceLeftWidth() {
    this.leftWidth = 60;
  }
  setLeftHeightBottom(marginFromBottom = 25) {
    this.leftHeight = this.setHeightToEndOfPage(marginFromBottom);
  }
  setRightHeightBottom() {
    this.rightHeight = this.setHeightToEndOfPage();
  }
  setHeightToEndOfPage(marginFromBottom = 5) {
    return this.pdf.internal.pageSize.height - marginFromBottom;
  }
  justifyRightSide(text) {
    this.justify(text, this.rightMargin, this.rightHeight, this.rightWidth);
  }
  justifyLeftSide(text) {
    this.justify(text, this.leftMargin, this.leftHeight, this.leftWidth);
  }
  justify(text, xStart, yStart, textWidth) {
    text = text.replace(/(?:\r\n|\r|\n)/g, " ");
    text = text.replace(/ +(?= )/g, "");
    const lineHeight = this.pdf.getTextDimensions("a").h * this.lineHeight;
    const words = text.split(" ");
    let lineNumber = 0;
    let wordsInfo = [];
    let lineLength = 0;
    for (const word of words) {
      const wordLength = this.pdf.getTextWidth(word + " ");
      if (wordLength + lineLength > textWidth) {
        this.writeLine(
          wordsInfo,
          lineLength,
          lineNumber++,
          xStart,
          yStart,
          lineHeight,
          textWidth
        );
        wordsInfo = [];
        lineLength = 0;
      }
      wordsInfo.push({ text: word, wordLength });
      lineLength += wordLength;
    }
    if (wordsInfo.length > 0) {
      this.writeLastLine(wordsInfo, xStart, yStart, lineNumber, lineHeight);
    }
  }
  writeLine(
    wordsInfo,
    lineLength,
    lineNumber,
    xStart,
    yStart,
    lineHeight,
    textWidth
  ) {
    const wordSpacing = (textWidth - lineLength) / (wordsInfo.length - 1);
    let x = xStart;
    const y = yStart + lineNumber * lineHeight;
    for (const wordInfo of wordsInfo) {
      this.pdf.text(wordInfo.text, x, y);
      x += wordInfo.wordLength + wordSpacing;
    }
  }
  writeLastLine(wordsInfo, xStart, yStart, lineNumber, lineHeight) {
    const line = wordsInfo.map(x => x.text).join(" ");
    this.pdf.text(line, xStart, yStart + lineNumber * lineHeight);
    this[`increment${this.activeColumn}Height`](lineNumber * lineHeight);
  }
  splitTextToSize(text, size) {
    return this.pdf.splitTextToSize(text, size);
  }
  splitTextLeft(text) {
    return this.splitTextLeft(text, 50);
  }
  addTextLeft(text) {
    this.addText(this.leftMargin, this.leftHeight, this.leftWidth, text);
  }
  addTextRight(text, margin = this.rightMargin) {
    this.addText(margin, this.rightHeight, this.rightWidth, text);
  }
  addTextLastCol(text, margin = this.lastColMargin, lineHeight = 1.8) {
    if (lineHeight !== 1.8) {
      this.addText(margin, this.lastColHeight - ((1.8 - lineHeight) * 2), this.lastColWidth, text, lineHeight);
    } else {
      this.addText(margin, this.lastColHeight, this.lastColWidth, text, lineHeight);
    }
  }
  addText(leftMargin, verticalHeight, maxWidth, text, lineHeight = 1.8) {
    this.pdf.text(leftMargin, verticalHeight, text, {
      maxWidth: maxWidth,
      align: "justify",
      baseline: "middle",
      lineHeightFactor: lineHeight
    });
  }
  addImage(image, xStart, yStart, width, height) {
    this.pdf.addImage(image, "PNG", xStart, yStart, width, height);
  }
  drawRoundedRectangle(pageNumber) {
    if (pageNumber == 1) {
      this.pdf.setDrawColor("#58595B");
      this.pdf.setFillColor(246, 246, 246);
      this.pdf.roundedRect(5, 40, 68, 50, 3, 3, "FD");
    }
    if (pageNumber == 2) {
      this.pdf.setDrawColor("#58595B");
      this.pdf.setFillColor(246, 246, 246);
      this.pdf.roundedRect(5, 5, 200, 45, 3, 3, "FD");
    }
  }
  drawCircle(color, size, side, level = "") {
    let x;
    let y;
    if (side === "left") {
      x = this.leftMargin;
      y = this.leftHeight;
    }
    if (side === "right") {
      x = this.rightMargin;
      y = this.rightHeight;
    }
    if (side === "last") {
      x = this.lastColMargin;
      y = this.lastColHeight;
    }
    let fillCol = "";
    if (color === "green") {
      fillCol = "#92D284";
    }
    if (color === "red") {
      fillCol = "#FF968F";
    }
    if (color === "dark-red") {
      fillCol = "#E7554A";
    }
    if (color === "yellow") {
      fillCol = "#F9C750";
    }
    if (color === "grey") {
      fillCol = "#D6D6D6";
    }
    this.pdf.setDrawColor(fillCol);
    this.pdf.setFillColor(fillCol);
    this.pdf.circle(x, y - 1, size, "FD");

    // Add level to the circle
    if (color === "green") {
      this.pdf.setTextColor("#000");
    }
    if (color === "red") {
      this.pdf.setTextColor("#FFF");
    }
    if (color === "dark-red") {
      this.pdf.setTextColor("#FFF");
    }
    if (color === "yellow") {
      this.pdf.setTextColor("#000");
    }
    if (color === "grey") {
      this.pdf.setTextColor("#000");
    }

    this.setFontStyle("Medium");
    this.pdf.setFontSize(7);
    this.pdf.text(level.toString(), x - 0.7, y);
    this.pdf.setTextColor("#373739");
  }
  drawRectangle(x_top = 0, y_top = 0, width = 100, height = 100) {
    this.pdf.setDrawColor(230, 230, 230);
    this.pdf.setFillColor(245, 245, 245);
    this.pdf.rect(x_top, y_top, width, height, "FD");
  }
  drawVerticalLine(x_top, y_top, x_bottom, y_bottom) {
    this.pdf.setDrawColor(230, 230, 230);
    this.pdf.line(x_top, y_top, x_bottom, y_bottom);
  }
  centerRight(text) {
    const currentFontSize = this.pdf.internal.getFontSize();
    const scaleFactor = this.pdf.internal.scaleFactor;
    const textWidth =
      (this.pdf.getStringUnitWidth(text) * currentFontSize) / scaleFactor;
    return 140 - textWidth / 2;
  }
  updateActiveColumn(column) {
    this.activeColumn = column;
  }
  drawChart() {
    const canvas = document.createElement("canvas");
    canvas.width = 450 * 2;
    canvas.height = 220 * 2;
    let context = canvas.getContext("2d");
    new Chart(context, {
      type: "bar",
      data: {
        labels: this.threads,
        datasets: [
          {
            minBarLength: ctx => {
              const index = ctx.dataIndex;
              const value = ctx.dataset.data[index];
              if (value === 0) return 1;
              else if (value < 0) return 0;
            },
            label: "Initial results",
            data: this.assessmentResults[0],
            backgroundColor: "#D6D8E1",
            borderColor: "#D6D8E1",
            borderWidth: 1
          },
          {
            minBarLength: ctx => {
              const index = ctx.dataIndex;
              const value = ctx.dataset.data[index];
              if (value === 0) return 1;
              else if (value < 0) return 0;
            },
            label: "Re-assessment results ",
            data: this.assessmentResults[1],
            backgroundColor: "#599CE6",
            borderColor: "#599CE6",
            borderWidth: 1
          }
        ]
      },
      options: {
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                drawBorder: true,
                color: "rgba(225,78,202,0.1)"
              },
              scaleLabel: {
                display: false,
                labelString: "Learning Journey",
                // fontStyle: "bold",
                color: "#000",
                fontSize: "16"
              },
              ticks: {
                display: true,
                fontColor: "#000"
              }
            }
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: {
                drawBorder: true,
                color: "rgba(29,140,248,0.0)"
              },
              scaleLabel: {
                display: false,
                labelString: "Level Achieved",
                // fontStyle: "bold",
                color: "#000",
                fontSize: "16"
              },
              ticks: {
                display: true,
                beginAtZero: true,
                min: 0,
                max: this.assessmentTypeLevel,
                stepSize: 1,
                fontColor: "#000",
                callback: function (value) {
                  if (Number.isInteger(value)) {
                    return `${value}`;
                  }
                }
              }
            }
          ]
        },
        legend: {
          display: false
        },
        animation: {
          duration: 0
        },
        responsive: false,
        maintainAspectRatio: true
      }
    });
    return canvas.toDataURL("image/PNG", 1);
  }
  generateConcepts() {
    let conceptsText = [];
    this.threads.forEach((element, index) => {
      let thisThread = {
        concepts: [],
        title: ""
      };
      thisThread.title = element;
      let conceptResults = this.conceptResults(index + 1);
      conceptResults.forEach(el => {
        thisThread.concepts.push(el);
      });
      conceptsText.push(thisThread);
    });
    return conceptsText;
  }
  generateFeedback(threadStats) {
    let assessmentsComplete = threadStats.filter(
      thread => thread.assessmentComplete === true
    );
    let assessmentsIncomplete = threadStats.filter(
      thread => thread.assessmentComplete === false
    );
    let reassessmentsIncomplete = threadStats.filter(
      thread => thread.reassessmentComplete === false
    );
    let threadsFullyMastered = threadStats.filter(
      thread => thread.percentageMastered === 100
    );
    let threadsMastered = threadStats.filter(
      thread => thread.percentageMastered >= 75
    );
    let threadsNotMastered = threadStats.filter(
      thread =>
        thread.percentageMastered < 75 && thread.assessmentComplete === true
    );
    let hasImprovedThreads = threadStats.filter(
      thread => thread.hasImproved === true
    );
    let feedbackText = ``;
    if (assessmentsIncomplete.length === this.threads.length) {
      feedbackText += `${this.firstName} has not yet completed any assessments. Once an assessment is complete, their understanding will be analysed and their gaps identified. `;
    } else {
      //all crowns
      if (threadsFullyMastered.length === this.threads.length) {
        feedbackText += `Wow - ${this.firstName} is at the expected level in all journeys and has caught up every concept! There are no gaps remaining. `;
      } else {
        // //any improvements
        // if (hasImprovedThreads && hasImprovedThreads.length > 0) {
        //   feedbackText += `Well done on improvements made thus far! `;
        // }
        //mastered threads
        if (threadsMastered && threadsMastered.length > 0) {
          if (threadsMastered.length === 1) {
            if (threadsMastered[0].percentageMastered === 100) {
              feedbackText += `${this.firstName} is at the expected level in ${threadsMastered[0].threadName}, and has no gaps remaining in this journey. `;
            } else {
              feedbackText += `${this.firstName} is at the expected level in ${threadsMastered[0].threadName}, but still has some gaps to catch up. `;
            }
          } else {
            feedbackText += `${this.firstName} is at the expected level in `;
            feedbackText += this.generateThreadFeedbackItems(threadsMastered);

            let hasGaps = threadsMastered.filter(thread => thread.percentageMastered < 100);

            if (hasGaps.length > 0) {
              feedbackText += `but still has some gaps to catch up. `;
            }
          }
        }
      }

      if (hasImprovedThreads && hasImprovedThreads.length === 1) {
        if (hasImprovedThreads[0].yearsImproved === 1) {
          feedbackText += `${this.firstName} has caught up ${hasImprovedThreads[0].yearsImproved} year in ${hasImprovedThreads[0].threadName}. `;
        } else {
          feedbackText += `${this.firstName} has caught up ${hasImprovedThreads[0].yearsImproved} years in ${hasImprovedThreads[0].threadName}. `;
        }
      } else if (hasImprovedThreads && hasImprovedThreads.length > 0) {
        feedbackText += `${this.firstName} has caught up `;
        hasImprovedThreads.forEach(thread => {
          if (thread.hasImproved) {
            // If last thread then add in and and remove the comma from the previous thread
            if (hasImprovedThreads.indexOf(thread) === hasImprovedThreads.length - 1) {
              feedbackText = feedbackText.substring(0, feedbackText.length - 2);
              feedbackText += ` and `;
            }
            if (thread.yearsImproved > 0) {
              if (thread.yearsImproved === 1) {
                feedbackText += `${thread.yearsImproved} year in ${thread.threadName}, `;
              } else {
                feedbackText += `${thread.yearsImproved} years in ${thread.threadName}, `;
              }
            }
          }
        });
        // Remove the last comma
        feedbackText = feedbackText.substring(0, feedbackText.length - 2);
        feedbackText += `. `;
      }

      //threads not mastered
      if (threadsNotMastered && threadsNotMastered.length === 1) {
        if (threadsNotMastered[0].maxThreadLevel - threadsNotMastered[0].threadLevel > 0) {
          if (threadsNotMastered[0].maxThreadLevel - threadsNotMastered[0].threadLevel === 1) {
            feedbackText += `${this.firstName} is ${threadsNotMastered[0].maxThreadLevel - threadsNotMastered[0].threadLevel} year behind in ${threadsNotMastered[0].threadName}. `;
          } else {
            feedbackText += `${this.firstName} is ${threadsNotMastered[0].maxThreadLevel - threadsNotMastered[0].threadLevel} years behind in ${threadsNotMastered[0].threadName}. `;
          }
        }
      } else if (threadsNotMastered && threadsNotMastered.length > 0) {
        // If there are threads that have not been mastered
        // Tell the learner that they are however many years behind in the threads that they have not mastered
        feedbackText += `${this.firstName} is `;
        threadsNotMastered.forEach(thread => {
          // If last thread then add in and and remove the comma from the previous thread
          if (threadsNotMastered.indexOf(thread) === threadsNotMastered.length - 1) {
            feedbackText = feedbackText.substring(0, feedbackText.length - 2);
            feedbackText += ` and `;
          }
          if (thread.maxThreadLevel - thread.threadLevel > 0) {
            if (thread.maxThreadLevel - thread.threadLevel === 1) {
              feedbackText += `${thread.maxThreadLevel - thread.threadLevel} year behind in ${thread.threadName}, `;
            } else {
              feedbackText += `${thread.maxThreadLevel - thread.threadLevel} years behind in ${thread.threadName}, `;
            }
          }
        });
        // Remove the last comma
        feedbackText = feedbackText.substring(0, feedbackText.length - 2);
        feedbackText += `. `;
      } else {
        //incomplete assessments
        if (assessmentsIncomplete.length > 0) {
          feedbackText += `${this.firstName} is encouraged to complete the initial assessment for ${assessmentsIncomplete[0].threadName}. `;
          // feedbackText += this.generateThreadFeedbackItems(assessmentsIncomplete);
        }
      }
    }

    feedbackText += `The journeys have been designed to work in order, as they build on each other (like a tower of learning).`;

    return feedbackText;
  }
  generateThreadFeedbackItems(threadItems) {
    let feedbackText = "";
    let counter = 1;
    threadItems.forEach(thread => {
      feedbackText += `${thread.threadName}`;
      if (counter < threadItems.length - 1) {
        feedbackText += `, `;
      } else if (counter === threadItems.length - 1) {
        if (threadItems.length === 2) {
          feedbackText += ` and `;
        } else {
          feedbackText += `, and `;
        }
      } else {
        feedbackText += `, `;
      }
      counter++;
    });
    return feedbackText;
  }
  getSchoolLogoUrl() {
    const schoolImagePath =
      "https://storage.googleapis.com/rl-image-bucket/school-logos/";
    let schoolFileName = this.school
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^\w-]+/g, "");
    return schoolImagePath + schoolFileName + ".png";
  }
  saveReport() {
    let learnerInfo =
      this.school + "-" + this.subject + "-Grade " + this.grade + "-";
    learnerInfo += this.firstName + " " + this.lastName;
    learnerInfo += "-Progress Report";
    this.pdf.save("ReflectiveLearning-" + learnerInfo);
  }
  getCurrentAssessment() {
    return this.currentAssessment;
  }
  getNumberOfConcepts(color) {
    switch (color) {
      case "green":
        return this.greenConcepts.toString();
      case "yellow":
        return this.yellowConcepts.toString();
      case "red":
        return this.redConcepts.toString();
      case "darkRed":
        return this.darkRedConcepts.toString();
    }
    return this.greyConcepts.toString();
  }
  // percentageMastered(threadId) {
  //   let totalGradeLevel = 0;

  //   let conceptMastery = this.assessmentStatus.find(thread => thread.threadId == threadId).feedback.conceptMastery;
  //   if (conceptMastery) {
  //     conceptMastery.forEach((element, key) => {
  //       totalGradeLevel += element;
  //     });
  //     let maxGradeLevel = 3 * conceptMastery.length;
  //     let percentage = (totalGradeLevel / maxGradeLevel) * 100;
  //     return Math.round(percentage);
  //   } else {
  //     return 0;
  //   }
  // }
  // trophyColour(threadId) {
  //   let colour = "";
  //   let percentage = this.percentageMastered(threadId);

  //   if (percentage < 75) {
  //     colour = "None";
  //   } else if (percentage < 90) {
  //     colour = "Bronze";
  //   } else if (percentage < 100) {
  //     colour = "Silver";
  //   } else {
  //     colour = "Gold";
  //   }

  //   return `${colour}`;
  // }
}

function convertImagetoBase64Uri(image) {
  let baseImage = new Image();
  baseImage.src = image;
  return baseImage;
}

const createPdf = (learnerObject, currentSubject) => {
  let report = new Report(learnerObject, currentSubject);

  const pageHeight = report.pdf.internal.pageSize.height || report.pdf.internal.pageSize.getHeight();
  const pageWidth = report.pdf.internal.pageSize.width || report.pdf.internal.pageSize.getWidth();

  // report.drawChart();
  /* Upload the correct images before drawing them */
  const logo = require("@/modules/global/assets/rl.png");
  const logoWhite = require("@/modules/global/assets/rl-logo-white.png");
  const signature = require("@/modules/global/assets/signature.png");
  const goldCrown = require("@/modules/global/assets/trophy_gold.png");
  const silverCrown = require("@/modules/global/assets/trophy_silver.png");
  const bronzeCrown = require("@/modules/global/assets/trophy_bronze.png");
  const greyCrown = require("@/modules/global/assets/trophy_grey.png");
  let levelsIcon;
  if (report.getCurrentAssessment() == "MATHS_JUNIOR_PRIMARY") {
    levelsIcon = require("@/modules/global/assets/level-icons-jp.png");
  }
  if (
    report.getCurrentAssessment() == "MATHS_SENIOR_PRIMARY" ||
    report.getCurrentAssessment() == "ENGLISH_SENIOR_PRIMARY"
  ) {
    levelsIcon = require("@/modules/global/assets/level-icons-sp.png");
  }
  if (
    report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH" ||
    report.getCurrentAssessment() == "ENGLISH_JUNIOR_HIGH"
  ) {
    levelsIcon = require("@/modules/global/assets/level-icons-jh.png");
  }
  const logoImageElement = convertImagetoBase64Uri(logo);
  const logoWhiteElement = convertImagetoBase64Uri(logoWhite);
  const signatureElement = convertImagetoBase64Uri(signature);
  const goldCrownElement = convertImagetoBase64Uri(goldCrown);
  const silverCrownElement = convertImagetoBase64Uri(silverCrown);
  const bronzeCrownElement = convertImagetoBase64Uri(bronzeCrown);
  const greyCrownElement = convertImagetoBase64Uri(greyCrown);
  const levelsIconElement = convertImagetoBase64Uri(levelsIcon);
  // const schoolLogoUrl = convertImagetoBase64Uri(report.getSchoolLogoUrl());

  /* Header */
  // report.pdf.advancedAPI(pdf => {
  //   pdf.addShadingPattern(
  //     "unique-pattern-key",
  //     new ShadingPattern(
  //       "axial",
  //       [0, 0, 200, 30],
  //       [{
  //         offset: 0,
  //         color: [74, 182, 249] //RGB as an array
  //       },
  //       {
  //         offset: 1.5,
  //         color: [177, 144, 254]
  //       }
  //       ]));
  //   pdf.roundedRect(5, 5, 200, 30, 3, 3).fill({
  //     key: 'unique-pattern-key',
  //     matrix: [0, 0, 0, 0, 0, 0, 0, 0]
  //   });
  // });
  report.pdf.setDrawColor(74, 182, 249);
  report.pdf.setFillColor(74, 182, 249);
  report.pdf.roundedRect(5, 5, 200, 30, 3, 3, "FD");
  report.setFontTo32();
  report.mediumFont();
  report.pdf.setTextColor("#FFFFFF");
  report.pdf.text("Progress Report", 10, 25);
  report.pdf.addImage(logoWhiteElement, "PNG", 150, 12, 48.7, 18.4);
  report.pdf.setTextColor("#373739");

  /* Left column Content */
  report.leftHeight = 42;
  report.drawRoundedRectangle(1);
  report.mediumFont();
  report.setFontTo14();
  report.incrementLeftHeight(8);
  report.addTextLeft(`${report.firstName} ${report.lastName}`);
  report.incrementLeftHeight(10);
  report.lightFont();
  report.setFontTo11();
  report.justifyLeftSide(`${report.subject}`);
  report.incrementLeftHeight(6);
  report.justifyLeftSide(`Grade ${report.grade}`);
  report.incrementLeftHeight(6);
  report.justifyLeftSide(`${report.school}`);
  report.incrementLeftHeight(7);
  report.addTextLeft(`Registered on ${report.subscriptionDate}`);
  report.incrementLeftHeight(6);
  report.justifyLeftSide(`Issued on ${report.reportIssueDate}`);
  report.incrementLeftHeight(20);
  report.mediumFont();
  report.setFontTo14();
  report.addTextLeft("LEVELS ACHIEVED");

  report.resetLeftMargin();
  let threadStats = report.assessmentStatus;
  for (let i = 0; i < report.threads.length; i++) {
    let threadId = i + 1;
    let currentThread = threadStats.find(thread => thread.threadId == threadId);
    const trophyColour = currentThread.trophyColour;

    let trophyAchieved = greyCrownElement;
    // if (currentThread.trophyAchieved) {
    //   trophyAchieved = goldCrownElement;
    // }

    if (trophyColour == "Gold") {
      trophyAchieved = goldCrownElement;
    }
    if (trophyColour == "Silver") {
      trophyAchieved = silverCrownElement;
    }
    if (trophyColour == "Bronze") {
      trophyAchieved = bronzeCrownElement;
    }
    report.incrementLeftHeight(13);

    report.setFontTo10();
    report.lightFont();

    var text = trophyColour;
    var fontSize = 10; // Adjust the font size as needed
    var textHeight = fontSize;

    // Calculate the center position
    let xOffset = 5;
    let yOffset = 0;
    var centerY = 1 + textHeight / 2;

    if (trophyColour == "Gold") {
      xOffset += 0;
    }
    if (trophyColour == "Silver") {
      xOffset += 1;
      yOffset += 1;
    }
    if (trophyColour == "Bronze") {
      xOffset += 2;
      yOffset += 2;
    }

    // Save the current canvas state
    report.pdf.saveGraphicsState();

    // Move the origin to the center position
    report.pdf.text(text.toLowerCase(), report.leftMargin + xOffset, report.leftHeight + centerY + yOffset, {
      align: 'center',
      angle: 90
    });

    // Restore the canvas state
    report.pdf.restoreGraphicsState();

    report.mediumFont();
    report.setFontTo11();
    report.resetLeftWidth();
    report.pdf.addImage(
      trophyAchieved,
      "PNG",
      report.leftMargin + 4,
      report.leftHeight - 4,
      7,
      12
    );
    report.reduceLeftWidth();
    report.leftMargin = 12;
    report.shiftLeftMargin();
    report.incrementLeftHeight(1);
    report.justifyLeftSide(`${report.threads[i]} `);
    // report.lightFont();
    report.incrementLeftHeight(5);
    report.addTextLeft(`Level ${currentThread.threadLevel}`);
    report.setFontTo10();

    // if (currentThread.assessmentComplete) {
    //   report.addTextLeft("Initial assessment complete");
    // } else {
    //   report.addTextLeft("No initial assessment complete");
    // }
    // report.incrementLeftHeight(5);
    // if (currentThread.reassessmentComplete) {
    //   report.addTextLeft(
    //     currentThread.reassessmentCompleteCount + " Re-assessments complete"
    //   );
    // } else {
    //   report.addTextLeft("No re-assessment complete");
    // }
    report.resetLeftMargin();
  }

  report.setLeftHeightBottom(37);
  report.resetLeftMargin();
  report.shiftLeftMargin();
  // report.pdf.addImage(
  //   schoolLogoUrl,
  //   "PNG",
  //   report.leftMargin + 3,
  //   report.leftHeight,
  //   35,
  //   35
  // );

  /* Right column Content */
  report.rightHeight = 25;
  report.updateActiveColumn("Right");
  const introSentence1 = `The Reflective Learning programme focuses on fixing the learning gaps in each student’s basic understanding. These gaps hold students back from learning new work, and make it difficult for students to succeed. We help each student by only teaching what they’re missing, and provide a path for them to catch up to the level of understanding expected.`;
  const introSentence2 = `The goal is to reach Level ${report.assessmentTypeLevel} across all ${report.threads.length} learning journeys. Below is ${report.firstName}'s progress so far.`;
  const graphInstructions = `Each bar represents the level achieved in each of the Learning Journeys. The goal is to reach Level ${report.assessmentTypeLevel}. The grey bars show the level achieved in the first baseline assessment. Blue bars indicate the improvement and new mastery they have achieved in any following assessments (after completing catch up lessons).`;
  const pageBottomText =
    "Learn more about what we do at reflectivelearning.co.za";
  report.drawVerticalLine(76, 40, 76, 265);
  report.incrementRightHeight(20);
  report.setFontTo10();
  report.lightFont();
  report.justifyRightSide(introSentence1);
  report.incrementRightHeight(10);
  report.justifyRightSide(introSentence2);
  report.setFontTo14();
  report.mediumFont();
  report.incrementRightHeight(10);
  report.addTextRight("RESULTS & PROGRESS");
  report.incrementRightHeight(5);

  report.pdf.addImage(
    report.chart,
    "PNG",
    report.rightMargin,
    report.rightHeight,
    120,
    60
  );
  report.setFontTo8();
  report.mediumFont();
  report.incrementRightHeight(65);
  report.addTextRight("Reading this graph: ");
  report.incrementRightHeight(5);
  report.lightFont();
  report.justifyRightSide(graphInstructions);
  report.incrementRightHeight(10);
  report.setFontTo14();
  report.mediumFont();
  report.addTextRight("FEEDBACK");
  report.incrementRightHeight(6);
  report.setFontTo12();
  report.lightFont();

  let feedbackText = report.generateFeedback(threadStats);
  report.setFontTo10();
  report.justifyRightSide(feedbackText);
  report.incrementRightHeight(10);
  report.justifyRightSide(
    `It is important to earn a trophy in all learning journeys, because this is the basic level of understanding expected of them. This means that they understand the fundamental concepts and are in a position to successfully learn and perform in class, and beyond.`
  );
  report.incrementRightHeight(10);
  report.justifyRightSide(
    `The ultimate goal is to catch up all gaps (earn a gold trophy in all of the journeys). At this point all of the building blocks that are required are in place, and there are no gaps holding them back.`
  );
  // report.incrementRightHeight(20);
  // report.addTextRight(
  //   "Yours in learning",
  //   report.centerRight("Yours in learning")
  // );
  // report.incrementRightHeight(3);
  // report.pdf.addImage(signatureElement, "PNG", 130, report.rightHeight, 20, 10);
  // report.incrementRightHeight(12);
  // report.addTextRight("Tracey Butchart", report.centerRight("Tracey Butchart"));
  // report.setRightHeightBottom();
  // report.mediumFont();
  // report.addTextRight(pageBottomText, report.centerRight(pageBottomText));

  /* Footer */
  report.pdf.setDrawColor(223, 243, 255);
  report.pdf.setFillColor(223, 243, 255);
  report.pdf.roundedRect(5, 271, 200, 15, 3, 3, "FD");
  report.setFontTo11();
  report.mediumFont();
  report.pdf.setTextColor(89, 156, 230);
  report.pdf.text(
    `Would you like more detail?`,
    pageWidth / 2,
    pageHeight - 20,
    { align: "center" }
  );
  report.pdf.setTextColor("#373739");
  report.lightFont();
  report.pdf.text(
    `${report.firstName} can invite you to create an account by clicking on ‘Add an Observer’ in their menu`,
    pageWidth / 2,
    pageHeight - 15,
    { align: "center" }
  );
  report.pdf.text(
    `Or you can learn more about what we do by visiting reflectivelearning.co.za`,
    pageWidth / 2,
    pageHeight - 5,
    { align: "center" }
  );

  // Page 2
  report.pdf.addPage("a4", "p");

  // Page 2 Header
  report.drawRoundedRectangle(2);
  report.rightMargin = 45;
  report.rightHeight = 12;
  report.setFontSize(22);
  report.mediumFont();
  report.addText(45, 18, 200, "LEVEL ACHIEVED PER CONCEPT");
  report.pdf.addImage(
    levelsIconElement,
    "PNG",
    report.leftMargin - 8,
    report.rightHeight + 8,
    190,
    24
  );

  // Drawing concepts with colored circles
  let concepts = report.generateConcepts();
  if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
    report.leftHeight = 60;
  } else {
    report.leftHeight = 60;
  }

  report.rightHeight = report.leftHeight;
  report.lastColHeight = report.leftHeight;
  report.leftWidth = 50;
  report.rightMargin = 80;
  report.rightWidth = report.leftWidth;
  report.lastColWidth = report.leftWidth;
  report.lastColMargin = 150;
  // if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
  //   report.rightWidth = 80;
  //   report.rightMargin = 67;
  //   report.lastColMargin = 152;
  //   report.leftMargin = 8;
  // } else if (report.getCurrentAssessment() == "MATHS_SENIOR_PRIMARY") {
  //   report.rightWidth = 80;
  //   report.rightMargin = 66;
  //   report.leftMargin = 9;
  // } else {
  //   report.rightWidth = report.leftWidth;
  // }

  report.rightWidth = 80;
  report.rightMargin = 67;
  report.lastColMargin = 152;
  report.leftMargin = 8;

  concepts.forEach((thread, index) => {
    report.mediumFont();
    report.setFontTo12();

    if (
      (index + 1) % 3 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_SENIOR_PRIMARY")
    ) {
      report.lastColMargin -= 3;
      report.addTextLastCol(thread.title, report.lastColMargin, 1.8);
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementLastColHeight(6);
      } else {
        report.incrementLastColHeight(6);
      }

      report.lastColMargin += 3;
    } else if (
      (index + 1) % 2 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_JUNIOR_PRIMARY")
    ) {
      report.rightMargin -= 3;
      report.addTextRight(thread.title);
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementRightHeight(6);
      } else {
        report.incrementRightHeight(6);
      }

      report.rightMargin += 3;
    } else if (index % 2 == 0) {
      report.leftMargin -= 3;
      report.addTextLeft(thread.title);
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementLeftHeight(6);
      } else {
        report.incrementLeftHeight(6);
      }

      report.leftMargin += 3;
    }
    report.lightFont();

    if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
      report.setFontSize(8);
    } else {
      report.setFontTo8();
    }

    if (
      (index + 1) % 3 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_SENIOR_PRIMARY")
    ) {
      thread.concepts.forEach(concept => {
        report.drawCircle(concept.color, 2, "last", concept.mastery);
        report.lastColMargin += 5;

        report.addTextLastCol(concept.name, report.lastColMargin, 1.0);
        if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
          report.incrementLastColHeight(6);
        } else {
          report.incrementLastColHeight(6);
        }
        report.lastColMargin -= 5;
      });
    } else if (
      (index + 1) % 2 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_JUNIOR_PRIMARY")
    ) {
      thread.concepts.forEach(concept => {
        report.drawCircle(concept.color, 2, "right", concept.mastery);
        report.rightMargin += 5;
        report.addTextRight(concept.name);
        if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
          report.incrementRightHeight(6);
        } else {
          report.incrementRightHeight(6);
        }

        report.rightMargin -= 5;
      });
    } else if (index % 2 == 0) {
      thread.concepts.forEach(concept => {
        report.drawCircle(concept.color, 2, "left", concept.mastery);
        report.leftMargin += 5;
        report.addTextLeft(concept.name);
        if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
          report.incrementLeftHeight(6);
        } else {
          report.incrementLeftHeight(6);
        }

        report.leftMargin -= 5;
      });
    }

    if (
      (index + 1) % 3 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_SENIOR_PRIMARY")
    ) {
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementLastColHeight(5);
      } else {
        report.incrementLastColHeight(5);
      }
    } else if (
      (index + 1) % 2 == 0 ||
      (index == 6 && report.getCurrentAssessment() == "MATHS_JUNIOR_PRIMARY")
    ) {
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementRightHeight(5);
      } else {
        report.incrementRightHeight(5);
      }
    } else if (index % 2 == 0) {
      if (report.getCurrentAssessment() == "MATHS_JUNIOR_HIGH") {
        report.incrementLeftHeight(5);
      } else {
        report.incrementLeftHeight(5);
      }
    }
  });

  // Drawing number of concept values
  report.mediumFont();
  report.setFontSize(22);
  // report.rightHeight = 39;
  // report.rightMargin = 46;
  report.rightHeight = 44.4;
  report.rightMargin = 46;
  report.addTextRight(report.getNumberOfConcepts("green"));
  report.rightMargin += 34;
  report.addTextRight(report.getNumberOfConcepts("yellow"));
  if (report.getNumberOfConcepts("red") >= 10) {
    report.rightMargin += 32;
  } else {
    report.rightMargin += 33;
  }

  report.addTextRight(report.getNumberOfConcepts("red"));
  report.rightMargin += 36;
  report.addTextRight(report.getNumberOfConcepts("darkRed"));
  report.rightMargin += 32;
  report.addTextRight(report.getNumberOfConcepts("grey"));
  // report.incrementLeftHeight(-200);
  // report.addTextLeft(concepts);
  report.saveReport();
  // Display the pdf
  // report.pdf.output("dataurlnewwindow");
};

export { createPdf };
